import { ProductBacklogItemEntity } from '../../../domain/entity/ProductBacklogEntity'
import { ProjectPlanCumulation } from '../../../lib/functions/projectPlan'
import { SprintDetail } from '../../../lib/functions/sprint'
import { UserBasic } from '../../../lib/functions/user'
import {
  createRowByResponse as createWbsItemRowByResponse,
  WbsItemRow,
} from '../../../lib/functions/wbsItem'
import { generateUuid } from '../../../utils/uuids'
import { TreeRow } from '../../containers/BulkSheetView/model'
import {
  getWorkloadUnitValue,
  round,
  WorkloadUnit,
} from '../../../lib/functions/workload'
import { CommentSummary } from '../../../store/comments'
import { AttachmentSummary } from '../../../utils/attachment'
import { WbsItemTypeVO } from '../../../domain/value-object/WbsItemTypeVO'
import { SprintProductItem } from '../../../domain/value-object/SprintProductItem'
import * as SprintAssignmentService from '../../../domain/service/SprintAssignmentService'

/**
 * SprintProductItemDetail は SprintAssignment の型エイリアス
 * 後方互換性のために維持
 */
export type SprintProductItemDetail = SprintProductItem

// [Row models]
// SprintRow is a parent row.
export interface SprintRow extends TreeRow {
  name: string
  rowType: 'sprint'
  isUnset: boolean
  sprintDetail?: SprintDetail
}
// ProductBacklogItemRow is a child row.
export interface ProductBacklogItemRow extends TreeRow {
  originalUuid: string
  wbsItem: WbsItemRow
  cumulation?: ProjectPlanCumulation
  rowType: 'productBacklogItem'

  // 以下はこの行の親スプリントに対応するSprintProductItemの値
  // PBI担当者 (Sprint×PBI単位)
  assignee?: UserBasic | null

  // スプリントゴール (Sprint×PBI単位)
  sprintGoal?: string | null

  // 1スプリントあたりの見積工数 (Sprint×PBI単位)
  estimatedHourPerSprint?: number

  projectPlanPath: string
  parentWbsItem?: {
    uuid: string
    projectUuid: string
    displayName: string
    wbsItemType: WbsItemTypeVO
  }
  commentSummary?: CommentSummary
  deliverableAttachmentSummary?: AttachmentSummary
  sprintProductItems: SprintProductItemDetail[]
}

// [SprintRow factory methods]
export const createUnsetSprintRow = (): SprintRow => {
  const rowUuid = generateUuid()
  return {
    uuid: rowUuid,
    name: 'スプリント未設定',
    treeValue: [rowUuid],
    rowType: 'sprint',
    isUnset: true,
  }
}
export const UNSET_SPRINT_ROW: SprintRow = createUnsetSprintRow()
export const createSprintRowByResponse = (sprint: SprintDetail): SprintRow => {
  return {
    name: sprint.name,
    uuid: sprint.uuid,
    treeValue: [sprint.uuid],
    rowType: 'sprint',
    isUnset: false,
    sprintDetail: sprint,
  }
}

// [ProductBacklogItemRow factory methods]
/**
 * ProductBacklogItemRowを作成する
 * @param sprintUuid スプリントUUID
 * @param productBacklogItem PBIエンティティ
 * @param spi SprintProductItem（スプリント未設定の場合はundefined）
 * @param sharedSprintProductItems 共有されるsprintProductItems配列（指定時はこれを使用、未指定時は新規作成）
 * @param sharedWbsItem 共有されるwbsItemオブジェクト（指定時はこれを使用、未指定時は新規作成）
 */
export const createProductBacklogItemRowByResponse = (
  sprintUuid: string,
  productBacklogItem: ProductBacklogItemEntity,
  spi?: SprintProductItemDetail, // undefinedのケースはスプリント未設定の場合
  sharedSprintProductItems?: SprintProductItemDetail[],
  sharedWbsItem?: WbsItemRow
): ProductBacklogItemRow => {
  const parentWbsItem = productBacklogItem.parentWbsItem
  // SprintProductItemのUUIDを行UUIDとして使用（複数担当者対応）
  const rowUuid = spi?.uuid || productBacklogItem.uuid
  const wbsItem =
    sharedWbsItem ?? createWbsItemRowByResponse(productBacklogItem.deliverable)
  return {
    rowType: 'productBacklogItem',
    uuid: rowUuid,
    originalUuid: productBacklogItem.uuid,
    treeValue: [sprintUuid, rowUuid],
    wbsItem,

    // PBI担当者 (Sprint×PBI単位) - 該当スプリントのSprintProductItemから取得
    assignee: spi?.assignee,

    // スプリントゴール (Sprint×PBI単位) - 該当スプリントのSprintProductItemから取得
    sprintGoal: spi?.sprintGoal,

    // 1スプリントあたりの見積工数 (Sprint×PBI単位) - 該当スプリントのSprintProductItemから取得
    estimatedHourPerSprint: spi?.estimatedHour,

    projectPlanPath: productBacklogItem.projectPlanPath,
    parentWbsItem: parentWbsItem
      ? {
          uuid: parentWbsItem.uuid,
          projectUuid: parentWbsItem.projectUuid,
          displayName: parentWbsItem.displayName,
          wbsItemType: new WbsItemTypeVO(parentWbsItem.typeDto),
        }
      : undefined,
    cumulation: productBacklogItem.cumulation,
    commentSummary: productBacklogItem.commentSummary,
    deliverableAttachmentSummary:
      productBacklogItem.deliverableAttachmentSummary,
    // sharedSprintProductItemsが渡された場合はそれを使用（同一originalUuidの行間で共有）
    // 渡されない場合は新規作成（後方互換性のため）
    sprintProductItems:
      sharedSprintProductItems ??
      (productBacklogItem.sprintProductItems
        ? productBacklogItem.sprintProductItems.map(spi => ({
            uuid: spi.uuid,
            sprintUuid: spi.sprintUuid,
            estimatedHour: spi.estimatedHour,
            taskEstimatedHour: spi.taskEstimatedHour,
            displayOrder: spi.displayOrder ?? 0,
            lockVersion: spi.lockVersion,
            assignee: spi.assignee,
            sprintGoal: spi.sprintGoal,
          }))
        : []),
    editedData: {}, // Initialize to share edited data with the same pbi rows associated with multiple sprints.
  }
}

/**
 * スプリント内の最大displayOrderを取得する
 */
export const getMaxDisplayOrderInSprint = (
  data: ProductBacklogRow[],
  sprintUuid: string
): number => {
  let maxOrder = 0
  for (const row of data) {
    if (row.rowType !== 'productBacklogItem') continue
    const pbiRow = row
    for (const spi of pbiRow.sprintProductItems || []) {
      if (spi.sprintUuid === sprintUuid && (spi.displayOrder ?? 0) > maxOrder) {
        maxOrder = spi.displayOrder ?? 0
      }
    }
  }
  return maxOrder
}

/**
 * スプリント内で行をコピーする
 * 複数スプリント紐付け、同一スプリント内担当者追加で共通利用
 * 新しいSprintProductItem用に新規UUIDを生成する
 */
export const copyProductBacklogItemRowForDuplicateInSameSprint = (
  sprintUuid: string,
  row: ProductBacklogItemRow,
  allData: ProductBacklogRow[]
): ProductBacklogItemRow => {
  const rowUuid = generateUuid()
  const sourceSprintProductItem = row.sprintProductItems.find(
    spi => spi.uuid === row.uuid
  )
  // 対象スプリント内の最大displayOrderを取得して末尾に追加
  const maxDisplayOrder = getMaxDisplayOrderInSprint(allData, sprintUuid)
  const addedSprintProductItem: SprintProductItemDetail = {
    uuid: rowUuid,
    sprintUuid,
    estimatedHour: 0,
    taskEstimatedHour: sourceSprintProductItem?.taskEstimatedHour || 0,
    displayOrder: maxDisplayOrder + 1,
    lockVersion: 0,
    assignee: null, // undefinedではなくnullに統一（行レベルと整合性を取る）
    sprintGoal: sourceSprintProductItem?.sprintGoal,
  }

  row.sprintProductItems.push(addedSprintProductItem)

  return {
    ...row,
    uuid: rowUuid,
    treeValue: [sprintUuid, rowUuid],
    assignee: null,
    sprintGoal: sourceSprintProductItem?.sprintGoal,
    sprintProductItems: row.sprintProductItems,
    added: true,
    edited: true,
    editedData: {},
  }
}

export type ProductBacklogRow = SprintRow | ProductBacklogItemRow

// [Row methods]
export const isSprintRow = (row: ProductBacklogRow) => {
  return row.rowType === 'sprint'
}
export const isUnsetSprintRow = (row: ProductBacklogRow) => {
  return isSprintRow(row) && row.isUnset
}
export const isUnsetSprintRowUuid = (uuid: string) => {
  return UNSET_SPRINT_ROW.uuid === uuid
}
export const isProductBacklogItemRow = (row: ProductBacklogRow) => {
  return row.rowType === 'productBacklogItem'
}
export const getWorkload = (
  pbiRow: ProductBacklogItemRow,
  workloadUnit: WorkloadUnit
): number => {
  return getWorkloadUnitValue(pbiRow.wbsItem.estimatedWorkload, workloadUnit)
}
export const getWorkloadDistributedToMultiSprints = (
  pbiRow: ProductBacklogItemRow,
  workloadUnit: WorkloadUnit
): number => {
  const workload = getWorkloadUnitValue(
    pbiRow.wbsItem.estimatedWorkload,
    workloadUnit
  )
  const sprintCount =
    pbiRow.wbsItem.sprints && pbiRow.wbsItem.sprints.length > 0
      ? pbiRow.wbsItem.sprints.length
      : 1

  return round(workload / sprintCount)
}
// 指定されたスプリントのSPI全て消す
export const removeSprintOfRow = (
  row: ProductBacklogRow,
  sprint: SprintDetail
) => {
  if (!isProductBacklogItemRow(row)) return
  const pbiRow = row
  // editedDataに変更前の値を記録
  if (!pbiRow.editedData) {
    pbiRow.editedData = {}
  }
  const beforeSprint = row.wbsItem.sprint
  const beforeSprints = row.wbsItem.sprints
  if (!beforeSprints || beforeSprints.length === 0) return

  const afterSprints = beforeSprints.filter(s => s.uuid !== sprint.uuid)
  // Do nothing if the sprint is not associated.
  if (beforeSprints.length === afterSprints.length) return

  if (!pbiRow.editedData.hasOwnProperty('wbsItem.sprint')) {
    pbiRow.editedData['wbsItem.sprint'] = beforeSprint
    pbiRow.editedData['wbsItem.sprints'] = beforeSprints
  }

  pbiRow.edited = true
  pbiRow.wbsItem.sprint = afterSprints.length > 0 ? afterSprints[0] : undefined
  pbiRow.wbsItem.sprints = afterSprints
  pbiRow.treeValue = [
    afterSprints.length === 0 ? UNSET_SPRINT_ROW.uuid : '',
    pbiRow.uuid,
  ]

  // ドメインサービスを使用してSPIを削除
  const newAssignments = SprintAssignmentService.removeSprintAssignments(
    pbiRow.sprintProductItems || [],
    sprint.uuid
  )
  // 共有配列をミューテート（同じoriginalUuidの行で共有されるため）
  pbiRow.sprintProductItems.length = 0
  pbiRow.sprintProductItems.push(...newAssignments)
}
// 指定されたスプリントのSPI1つ消す
export const removeSprintProductItemOfRow = (
  row: ProductBacklogRow,
  spiUuid: string,
  sprintUuid: string
) => {
  if (!isProductBacklogItemRow(row)) return
  const pbiRow = row
  // editedDataに変更前の値を記録
  if (!pbiRow.editedData) {
    pbiRow.editedData = {}
  }
  const beforeSprint = row.wbsItem.sprint
  const beforeSprints = row.wbsItem.sprints
  if (!beforeSprints || beforeSprints.length === 0) return

  const beforeSpis = row.sprintProductItems
  if (!SprintAssignmentService.findAssignmentByUuid(beforeSpis, spiUuid)) return

  // ドメインサービスを使用してSPIを削除
  const newAssignments = SprintAssignmentService.removeSprintAssignmentByUuid(
    beforeSpis,
    spiUuid
  )
  // 共有配列をミューテート（同じoriginalUuidの行で共有されるため）
  pbiRow.sprintProductItems.length = 0
  pbiRow.sprintProductItems.push(...newAssignments)
  pbiRow.edited = true

  // 同じスプリントにまだ同じ成果物が残っているか？
  if (SprintAssignmentService.isAssignedToSprint(newAssignments, sprintUuid)) {
    return
  }
  const afterSprints = beforeSprints.filter(s => s.uuid !== sprintUuid)
  // Do nothing if the sprint is not associated.
  if (beforeSprints.length !== afterSprints.length) {
    if (!pbiRow.editedData.hasOwnProperty('wbsItem.sprint')) {
      pbiRow.editedData['wbsItem.sprint'] = beforeSprint
      pbiRow.editedData['wbsItem.sprints'] = beforeSprints
    }

    pbiRow.wbsItem.sprint =
      afterSprints.length > 0 ? afterSprints[0] : undefined
    pbiRow.wbsItem.sprints = afterSprints
  }

  pbiRow.treeValue = [
    afterSprints.length === 0 ? UNSET_SPRINT_ROW.uuid : '',
    pbiRow.uuid,
  ]
}

export const getSprintProductItemFromPbiRow = (
  pbiRow: ProductBacklogItemRow,
  sprintProductItemUuid: string
): SprintProductItemDetail | undefined => {
  return pbiRow.sprintProductItems?.find(
    spi => spi.uuid === sprintProductItemUuid
  )
}

export const createSprintProductItemKey = (
  sprintUuid: string,
  rowUuid?: string
): string => {
  // rowUuidがある場合は含めて一意にする（同一スプリント内の複数担当者対応）
  const suffix = rowUuid ? `.${rowUuid}` : ''
  return `sprintProductItem.${sprintUuid}${suffix}.estimatedHour`
}

export const createSprintProductItemAssigneeKey = (
  sprintUuid: string,
  rowUuid?: string
): string => {
  // rowUuidがある場合は含めて一意にする（同一スプリント内の複数担当者対応）
  const suffix = rowUuid ? `.${rowUuid}` : ''
  return `sprintProductItem.${sprintUuid}${suffix}.assignee`
}

export const createSprintProductItemSprintGoalKey = (
  sprintUuid: string,
  rowUuid?: string
): string => {
  // rowUuidがある場合は含めて一意にする（同一スプリント内の複数担当者対応）
  const suffix = rowUuid ? `.${rowUuid}` : ''
  return `sprintProductItem.${sprintUuid}${suffix}.sprintGoal`
}

// ============================================================================
// SPIベースの判定関数
// ドメイン層 (SprintAssignmentService) に委譲
// ============================================================================

/**
 * sprintProductItemsからスプリントUUIDのリストを取得
 */
export const getAssociatedSprintUuids = (
  row: ProductBacklogItemRow
): string[] => {
  return SprintAssignmentService.getAssignedSprintUuids(
    row.sprintProductItems || []
  )
}

/**
 * sprintProductItemsベースで複数スプリントに紐付いているか判定
 */
export const isAssociatedWithMultiSprintsBySpi = (
  row: ProductBacklogRow
): boolean => {
  if (!isProductBacklogItemRow(row)) return false
  return SprintAssignmentService.isAssignedToMultipleSprints(
    row.sprintProductItems || []
  )
}

/**
 * sprintProductItemsベースで指定スプリントに紐付いているか判定
 */
export const isAssociatedWithTheSprintBySpi = (
  row: ProductBacklogRow,
  sprintUuid: string
): boolean => {
  if (!isProductBacklogItemRow(row)) return false
  return SprintAssignmentService.isAssignedToSprint(
    row.sprintProductItems || [],
    sprintUuid
  )
}

/**
 * sprintProductItemsベースで何らかのスプリントに紐付いているか判定
 */
export const isAssociatedWithSomeSprintBySpi = (
  row: ProductBacklogRow
): boolean => {
  if (!isProductBacklogItemRow(row)) return false
  return SprintAssignmentService.isAssignedToAnySprint(
    row.sprintProductItems || []
  )
}

/**
 * sprintProductItemsベースでスプリント未紐付けか判定
 */
export const isNotAssociatedWithAnySprintBySpi = (
  row: ProductBacklogRow
): boolean => {
  if (!isProductBacklogItemRow(row)) return false
  return !SprintAssignmentService.isAssignedToAnySprint(
    row.sprintProductItems || []
  )
}

/**
 * 同一成果物・同一スプリント・同一担当者の重複をチェック
 * @returns 重複がある場合、重複している行のインデックス（1始まり）の配列を返す
 */
export const findDuplicateAssignments = (
  rows: ProductBacklogRow[]
): number[] => {
  const seen = new Map<string, number>() // key -> 最初に見つけた行のインデックス
  const duplicateRowIndices: number[] = []

  for (let i = 0; i < rows.length; i++) {
    const r = rows[i]
    if (!isProductBacklogItemRow(r)) continue
    const row = r
    const sprintUuid = row.treeValue[0]
    if (!sprintUuid || isUnsetSprintRowUuid(sprintUuid)) continue

    const assigneeUuid = row.assignee?.uuid
    if (!assigneeUuid) continue

    // キー: 成果物UUID + スプリントUUID + 担当者UUID
    const key = `${row.originalUuid}:${sprintUuid}:${assigneeUuid}`
    if (seen.has(key)) {
      duplicateRowIndices.push(i + 1) // 1始まりの行番号
    } else {
      seen.set(key, i)
    }
  }

  return duplicateRowIndices
}

/**
 * この行自身が指定スプリントに紐付いているか判定（行UUIDベース）
 * 共有配列でも正しく動作する（成果物単位ではなく行単位で判定）
 */
export const isRowAssociatedWithTheSprint = (
  row: ProductBacklogItemRow,
  sprintUuid: string
): boolean => {
  return SprintAssignmentService.isRowAssignedToSprint(
    row.sprintProductItems || [],
    row.uuid,
    sprintUuid
  )
}

// ============================================================================
// 行モデル操作関数
// 行モデル固有の処理（editedData管理、wbsItem.sprints同期、共有配列操作）を担当
// ドメインロジックはSprintAssignmentServiceに委譲
// ============================================================================

/**
 * SPIとwbsItem.sprintsの両方にスプリントを追加する
 * (整合性を保つため、両方を同時に更新)
 * @param forceNew trueの場合、既存SPIがあっても常に新規SPIを作成（同一スプリント内複数担当者用）
 * @param displayOrder 新規SPIのdisplayOrder（スプリント内の末尾に追加する場合はmax+1を渡す）
 */
export const addSprintToRowWithSpi = (
  row: ProductBacklogRow,
  sprint: SprintDetail,
  forceNew = false,
  displayOrder?: number
): SprintProductItemDetail | undefined => {
  if (!isProductBacklogItemRow(row)) return undefined
  const pbiRow = row

  // editedDataに変更前の値を記録
  if (!pbiRow.editedData) {
    pbiRow.editedData = {}
  }
  if (!pbiRow.editedData.hasOwnProperty('wbsItem.sprint')) {
    pbiRow.editedData['wbsItem.sprint'] = pbiRow.wbsItem.sprint
    pbiRow.editedData['wbsItem.sprints'] = pbiRow.wbsItem.sprints
  }

  // ドメインサービスでSPIを追加
  const { newAssignment } = SprintAssignmentService.addSprintAssignment(
    pbiRow.sprintProductItems || [],
    sprint,
    forceNew,
    displayOrder
  )

  // 現在のスプリント単位データを引き継ぐ（行レベルで保持している値を使用）
  newAssignment.assignee = pbiRow.assignee
  newAssignment.sprintGoal = pbiRow.sprintGoal
  newAssignment.estimatedHour = pbiRow.estimatedHourPerSprint ?? 0

  // 共有配列を維持するため、pushで追加（再代入しない）
  if (!pbiRow.sprintProductItems) {
    pbiRow.sprintProductItems = []
  }
  // 既存SPIと同じUUIDでなければ追加
  if (!pbiRow.sprintProductItems.some(spi => spi.uuid === newAssignment.uuid)) {
    pbiRow.sprintProductItems.push(newAssignment)
  }

  // wbsItem.sprint/sprintsも同期更新（互換性維持）
  // ※ forceNewの場合でもwbsItem.sprintsには重複追加しない
  const beforeSprints = pbiRow.wbsItem.sprints
  const alreadyHasSprint = beforeSprints?.some(s => s.uuid === sprint.uuid)
  const afterSprints = alreadyHasSprint
    ? beforeSprints!
    : beforeSprints
      ? [...beforeSprints, sprint]
      : [sprint]
  afterSprints.sort((a, b) => a.num - b.num)
  pbiRow.edited = true
  pbiRow.wbsItem.sprint = afterSprints.length > 0 ? afterSprints[0] : undefined
  pbiRow.wbsItem.sprints = afterSprints

  return newAssignment
}

/**
 * SPIとwbsItem.sprintsの両方でスプリントを設定する（上書き）
 * (整合性を保つため、両方を同時に更新)
 */
export const setSprintOfRowWithSpi = (
  row: ProductBacklogRow,
  sprint: SprintDetail | undefined
): SprintProductItemDetail | undefined => {
  if (!isProductBacklogItemRow(row)) return undefined
  const pbiRow = row

  // editedDataに変更前の値を記録
  if (!pbiRow.editedData) {
    pbiRow.editedData = {}
  }
  if (!pbiRow.editedData.hasOwnProperty('wbsItem.sprint')) {
    pbiRow.editedData['wbsItem.sprint'] = pbiRow.wbsItem.sprint
    pbiRow.editedData['wbsItem.sprints'] = pbiRow.wbsItem.sprints
  }

  pbiRow.edited = true

  // ドメインサービスでSPIを設定
  const { newAssignment } = SprintAssignmentService.setSprintAssignment(sprint)

  // 共有配列を維持するため、中身を置き換え（再代入しない）
  if (!pbiRow.sprintProductItems) {
    pbiRow.sprintProductItems = []
  }
  pbiRow.sprintProductItems.length = 0
  if (newAssignment) {
    // 現在のスプリント単位データを引き継ぐ（行レベルで保持している値を使用）
    newAssignment.assignee = pbiRow.assignee
    newAssignment.sprintGoal = pbiRow.sprintGoal
    newAssignment.estimatedHour = pbiRow.estimatedHourPerSprint ?? 0
    pbiRow.sprintProductItems.push(newAssignment)
    // 行UUIDをSPI UUIDに更新
    pbiRow.uuid = newAssignment.uuid
  } else {
    // 行UUIDをoriginalUuidに戻す
    pbiRow.uuid = pbiRow.originalUuid
  }

  // wbsItem.sprint/sprintsも同期更新（互換性維持）
  pbiRow.wbsItem.sprint = sprint ?? undefined
  pbiRow.wbsItem.sprints = sprint ? [sprint] : []
  pbiRow.treeValue = [sprint?.uuid || UNSET_SPRINT_ROW.uuid, pbiRow.uuid]

  // スプリント未割り当てへの移動時はスプリント単位データをクリア
  if (!sprint) {
    pbiRow.sprintGoal = undefined
    pbiRow.assignee = undefined
    pbiRow.estimatedHourPerSprint = undefined
  }

  return newAssignment
}
